import React, { useState, useEffect, useCallback } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { ethers } from 'ethers';

const PRESALE_ABI = [
  "function buyTokens() external payable",
  "function getUserInfo(address user) external view returns (uint256 bnbPaid, uint256 tokensOwed, uint256 tokensClaimed)",
  "function getPresaleInfo() external view returns (uint256 price, uint256 totalTokens, uint256 sold, uint256 remaining, bool active, bool distributed)",
  "function claimTokens() external",
  "function isPresaleActive() external view returns (bool)",
  "function tokensDistributed() external view returns (bool)",
  "function owner() external view returns (address)",
  "function tokenAddress() external view returns (address)",
  "function startPresale() external",
  "function endPresale() external",
  "function distributeTokens() external",
  "function emergencyWithdraw() external",
  "event TokensPurchased(address indexed buyer, uint256 bnbAmount, uint256 tokens)",
  "event TokensDistributed(address indexed user, uint256 tokenAmount)"
];

const ERC20_ABI = [
  "function decimals() external view returns (uint8)",
  "function symbol() external view returns (string)",
  "function name() external view returns (string)"
];

const PresaleWidget = () => {
  const { account, balance, provider, signer, isConnected, connectWallet } = useWeb3();
  const [presaleInfo, setPresaleInfo] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [bnbAmount, setBnbAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isOwner, setIsOwner] = useState(false);
  const [tokenDecimals, setTokenDecimals] = useState(18); // Default to 18

  const contractAddress = process.env.REACT_APP_PRESALE_CONTRACT_ADDRESS;

  const getContract = () => {
    if (!signer || !contractAddress) return null;
    return new ethers.Contract(contractAddress, PRESALE_ABI, signer);
  };

  const checkOwnership = useCallback(async () => {
    if (!provider || !account || !contractAddress) return;

    try {
      const contract = new ethers.Contract(contractAddress, PRESALE_ABI, provider);
      const owner = await contract.owner();
      setIsOwner(owner.toLowerCase() === account.toLowerCase());
    } catch (error) {
      console.error('Error checking ownership:', error);
    }
  }, [provider, account, contractAddress]);

  const loadTokenDecimals = useCallback(async () => {
    if (!provider || !contractAddress) return;

    try {
      const contract = new ethers.Contract(contractAddress, PRESALE_ABI, provider);
      const tokenAddr = await contract.tokenAddress();
      
      if (tokenAddr && tokenAddr !== ethers.constants.AddressZero) {
        const tokenContract = new ethers.Contract(tokenAddr, ERC20_ABI, provider);
        const decimals = await tokenContract.decimals();
        setTokenDecimals(decimals);
        console.log('Token decimals loaded:', decimals);
      } else {
        console.warn('Token address not set in presale contract, using default 18 decimals');
        setTokenDecimals(18);
      }
    } catch (error) {
      console.error('Error loading token decimals:', error);
      // Default to 18 if there's an error
      setTokenDecimals(18);
    }
  }, [provider, contractAddress]);

  const loadPresaleInfo = useCallback(async () => {
    if (!provider || !contractAddress) return;

    try {
      console.log('Loading presale info for contract:', contractAddress);
      console.log('Provider:', provider);
      const contract = new ethers.Contract(contractAddress, PRESALE_ABI, provider);
      const info = await contract.getPresaleInfo();
      
      setPresaleInfo({
        price: ethers.utils.formatEther(info.price), // Price is in BNB (18 decimals)
        totalTokens: ethers.utils.formatUnits(info.totalTokens, tokenDecimals),
        sold: ethers.utils.formatUnits(info.sold, tokenDecimals),
        remaining: ethers.utils.formatUnits(info.remaining, tokenDecimals),
        active: info.active,
        distributed: info.distributed
      });
    } catch (error) {
      console.error('Error loading presale info:', error);
      
      if (error.message.includes('call revert')) {
        setError('Invalid contract address or contract not deployed. Please check your configuration.');
      } else if (error.message.includes('network')) {
        setError('Network error. Please check your internet connection and try again.');
      } else if (error.message.includes('timeout')) {
        setError('Request timeout. Please try again.');
      } else {
        setError(`Failed to load presale information: ${error.message || 'Unknown error occurred'}`);
      }
    }
  }, [provider, contractAddress, tokenDecimals]);

  const loadUserInfo = useCallback(async () => {
    if (!provider || !account || !contractAddress) return;

    try {
      const contract = new ethers.Contract(contractAddress, PRESALE_ABI, provider);
      const info = await contract.getUserInfo(account);
      
      setUserInfo({
        bnbPaid: ethers.utils.formatEther(info.bnbPaid), // BNB is always 18 decimals
        tokensOwed: ethers.utils.formatUnits(info.tokensOwed, tokenDecimals),
        tokensClaimed: ethers.utils.formatUnits(info.tokensClaimed, tokenDecimals)
      });
    } catch (error) {
      console.error('Error loading user info:', error);
      
      if (error.message.includes('call revert')) {
        console.warn('User info not available - user may not have participated in presale');
        setUserInfo({
          bnbPaid: '0',
          tokensOwed: '0',
          tokensClaimed: '0'
        });
      } else {
        console.error('Failed to load user info:', error);
      }
    }
  }, [provider, account, contractAddress, tokenDecimals]);

  const startPresale = async () => {
    if (!signer || !isOwner) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getContract();
      const tx = await contract.startPresale();
      setSuccess('Starting presale...');
      await tx.wait();
      setSuccess('Presale started successfully!');
      await loadPresaleInfo();
    } catch (error) {
      console.error('Error starting presale:', error);
      setError(error.message || 'Failed to start presale');
    } finally {
      setLoading(false);
    }
  };

  const endPresale = async () => {
    if (!signer || !isOwner) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getContract();
      const tx = await contract.endPresale();
      setSuccess('Ending presale...');
      await tx.wait();
      setSuccess('Presale ended successfully!');
      await loadPresaleInfo();
    } catch (error) {
      console.error('Error ending presale:', error);
      setError(error.message || 'Failed to end presale');
    } finally {
      setLoading(false);
    }
  };

  const distributeTokens = async () => {
    if (!signer || !isOwner) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getContract();
      const tx = await contract.distributeTokens();
      setSuccess('Distributing tokens...');
      await tx.wait();
      setSuccess('Tokens distributed successfully!');
      await loadPresaleInfo();
    } catch (error) {
      console.error('Error distributing tokens:', error);
      setError(error.message || 'Failed to distribute tokens');
    } finally {
      setLoading(false);
    }
  };

  const buyTokens = async () => {
    if (!signer || !bnbAmount) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getContract();
      const amount = ethers.utils.parseEther(bnbAmount);
      
      const balance = await signer.getBalance();
      if (balance.lt(amount)) {
        setError('Insufficient BNB balance. Please check your wallet.');
        return;
      }

      const presaleActive = await contract.isPresaleActive();
      if (!presaleActive) {
        setError('Presale is not active. Please try again later.');
        return;
      }

      const tx = await contract.buyTokens({ value: amount });
      setSuccess('Transaction sent! Waiting for confirmation...');
      
      await tx.wait();
      setSuccess(`Successfully purchased ${calculateTokens(bnbAmount)} ${process.env.REACT_APP_TOKEN_SYMBOL || 'TEG'} tokens!`);
      setBnbAmount('');
      
      await loadPresaleInfo();
      await loadUserInfo();
    } catch (error) {
      console.error('Error buying tokens:', error);
      
      if (error.message.includes('user rejected')) {
        setError('Transaction was cancelled by user.');
      } else if (error.message.includes('insufficient funds')) {
        setError('Insufficient BNB balance. Please add more BNB to your wallet.');
      } else if (error.message.includes('Presale is not active')) {
        setError('Presale is not active. Please try again later.');
      } else if (error.message.includes('Not enough tokens remaining')) {
        setError('Not enough tokens remaining in this presale.');
      } else if (error.message.includes('Presale sold out')) {
        setError('Presale is sold out. No more tokens available.');
      } else if (error.message.includes('Must send BNB')) {
        setError('Please enter a valid BNB amount.');
      } else {
        setError(`Transaction failed: ${error.message || 'Unknown error occurred'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const claimTokens = async () => {
    if (!signer) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getContract();
      
      const tokensDistributed = await contract.tokensDistributed();
      if (!tokensDistributed) {
        setError('Tokens have not been distributed yet. Please wait for the presale to end and tokens to be distributed.');
        return;
      }

      const userInfo = await contract.getUserInfo(account);
      if (userInfo.tokensOwed.eq(0)) {
        setError('No tokens available to claim.');
        return;
      }

      const tx = await contract.claimTokens();
      setSuccess('Claim transaction sent! Waiting for confirmation...');
      
      await tx.wait();
      setSuccess(`Successfully claimed ${ethers.utils.formatUnits(userInfo.tokensOwed, tokenDecimals)} ${process.env.REACT_APP_TOKEN_SYMBOL || 'TEG'} tokens!`);
      
      await loadUserInfo();
    } catch (error) {
      console.error('Error claiming tokens:', error);
      
      if (error.message.includes('user rejected')) {
        setError('Transaction was cancelled by user.');
      } else if (error.message.includes('Tokens not yet distributed')) {
        setError('Tokens have not been distributed yet. Please wait for the presale to end and tokens to be distributed.');
      } else if (error.message.includes('No tokens to claim')) {
        setError('No tokens available to claim for this address.');
      } else if (error.message.includes('Token transfer failed')) {
        setError('Token transfer failed. Please try again or contact support.');
      } else {
        setError(`Claim failed: ${error.message || 'Unknown error occurred'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateTokens = (bnbAmount) => {
    if (!presaleInfo || !bnbAmount) return '0';
    const tokens = (parseFloat(bnbAmount) / parseFloat(presaleInfo.price)).toFixed(2);
    return tokens;
  };

  useEffect(() => {
    if (isConnected) {
      const initialize = async () => {
        await loadTokenDecimals();
        checkOwnership();
      };
      initialize();
    }
  }, [isConnected, account, checkOwnership, loadTokenDecimals]);

  // Load presale info and user info when decimals are loaded or updated
  useEffect(() => {
    if (isConnected) {
      loadPresaleInfo();
      loadUserInfo();
    }
  }, [isConnected, account, tokenDecimals, loadPresaleInfo, loadUserInfo]);

  if (!contractAddress) {
    return (
      <div className="presale-widget">
        <div className="error">
          ⚠️ Contract address not configured. Please set REACT_APP_PRESALE_CONTRACT_ADDRESS in your .env file.
        </div>
        <div className="help-text">
          <p>To fix this issue:</p>
          <ol>
            <li>Copy <code>env.example</code> to <code>.env</code></li>
            <li>Set <code>REACT_APP_PRESALE_CONTRACT_ADDRESS</code> to your deployed contract address</li>
            <li>Restart the development server</li>
          </ol>
        </div>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="presale-widget">
        <div className="wallet-section">
          <h2>Connect Your Wallet</h2>
          <p>Connect your wallet to participate in the {process.env.REACT_APP_TOKEN_SYMBOL || 'TEG'} token presale</p>
          <button className="connect-button" onClick={connectWallet}>
            Connect Wallet
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="presale-widget">
      <div className="wallet-section">
        <div className="connected-info">
          <div className="address">Address: {account}</div>
          <div className="balance">Balance: {parseFloat(balance).toFixed(4)} BNB</div>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {presaleInfo && (
        <>
          <div className="status">
            <div className={`status ${presaleInfo.active ? 'active' : 'inactive'}`}>
              {presaleInfo.active ? 'Presale Active' : 'Presale Inactive'}
            </div>
          </div>

          <div className="presale-info">
            <h3>Presale Information</h3>
            <div className="info-row">
              <span className="info-label">Token:</span>
              <span className="info-value">{process.env.REACT_APP_TOKEN_SYMBOL || 'TEG'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Price:</span>
              <span className="info-value">{presaleInfo.price} BNB per {process.env.REACT_APP_TOKEN_SYMBOL || 'TEG'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Total Tokens:</span>
              <span className="info-value">{parseFloat(presaleInfo.totalTokens).toLocaleString()} {process.env.REACT_APP_TOKEN_SYMBOL || 'TEG'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Tokens Sold:</span>
              <span className="info-value">{parseFloat(presaleInfo.sold).toLocaleString()} {process.env.REACT_APP_TOKEN_SYMBOL || 'TEG'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Tokens Remaining:</span>
              <span className="info-value">{parseFloat(presaleInfo.remaining).toLocaleString()} {process.env.REACT_APP_TOKEN_SYMBOL || 'TEG'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Distribution Status:</span>
              <span className="info-value">{presaleInfo.distributed ? 'Distributed' : 'Pending'}</span>
            </div>
          </div>

          {isOwner && (
            <div className="admin-controls">
              <h3>Admin Controls</h3>
              <div className="admin-buttons">
                <button
                  className="admin-button start"
                  onClick={startPresale}
                  disabled={loading || presaleInfo.active}
                >
                  {loading && <span className="loading"></span>}
                  Start Presale
                </button>
                <button
                  className="admin-button end"
                  onClick={endPresale}
                  disabled={loading || !presaleInfo.active}
                >
                  {loading && <span className="loading"></span>}
                  End Presale
                </button>
                <button
                  className="admin-button distribute"
                  onClick={distributeTokens}
                  disabled={loading || presaleInfo.distributed || presaleInfo.active}
                >
                  {loading && <span className="loading"></span>}
                  Distribute Tokens
                </button>
              </div>
            </div>
          )}

          {presaleInfo.active && (
            <div className="purchase-section">
              <h3>Buy {process.env.REACT_APP_TOKEN_SYMBOL || 'PRE'} Tokens</h3>
              <div className="input-group">
                <label htmlFor="bnbAmount">BNB Amount:</label>
                <input
                  id="bnbAmount"
                  type="number"
                  step="0.001"
                  min="0"
                  value={bnbAmount}
                  onChange={(e) => setBnbAmount(e.target.value)}
                  placeholder="Enter BNB amount"
                />
              </div>
              {bnbAmount && (
                <div className="info-row">
                  <span className="info-label">You will receive:</span>
                  <span className="info-value">{calculateTokens(bnbAmount)} {process.env.REACT_APP_TOKEN_SYMBOL || 'TEG'}</span>
                </div>
              )}
              <button
                className="buy-button"
                onClick={buyTokens}
                disabled={loading || !bnbAmount || parseFloat(bnbAmount) <= 0}
              >
                {loading && <span className="loading"></span>}
                {loading ? 'Processing...' : `Buy ${process.env.REACT_APP_TOKEN_SYMBOL || 'TEG'} Tokens`}
              </button>
            </div>
          )}

          {userInfo && parseFloat(userInfo.bnbPaid) > 0 && (
            <div className="user-info">
              <h3>Your Purchase</h3>
              <div className="info-row">
                <span className="info-label">BNB Paid:</span>
                <span className="info-value">{parseFloat(userInfo.bnbPaid).toFixed(4)} BNB</span>
              </div>
              <div className="info-row">
                <span className="info-label">Tokens Owed:</span>
                <span className="info-value">{parseFloat(userInfo.tokensOwed).toFixed(2)} {process.env.REACT_APP_TOKEN_SYMBOL || 'TEG'}</span>
              </div>
              {presaleInfo.distributed && parseFloat(userInfo.tokensOwed) > 0 && (
                <button
                  className="claim-button"
                  onClick={claimTokens}
                  disabled={loading}
                >
                  {loading && <span className="loading"></span>}
                  {loading ? 'Claiming...' : 'Claim Tokens'}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PresaleWidget;


